<div class="va-page">
	<div class="va-header">
		<h2 class="va-title">
			<mat-icon class="va-title-icon">block</mat-icon>
			Void Backorder Auths
		</h2>
		<p class="va-subtitle">Load → Validate with Stripe → Void. Validation catches auths already cancelled or captured.</p>
	</div>

	<!-- DATE RANGE -->
	<div class="va-date-row">
		<mat-form-field appearance="outline" class="va-date-field">
			<mat-label>Auth date from</mat-label>
			<input matInput [matDatepicker]="pickerFrom" [(ngModel)]="dateFrom" placeholder="MM/DD/YYYY" readonly>
			<mat-datepicker-toggle matSuffix [for]="pickerFrom"></mat-datepicker-toggle>
			<mat-datepicker #pickerFrom></mat-datepicker>
		</mat-form-field>
		<mat-form-field appearance="outline" class="va-date-field">
			<mat-label>Auth date to</mat-label>
			<input matInput [matDatepicker]="pickerTo" [(ngModel)]="dateTo" placeholder="MM/DD/YYYY" readonly>
			<mat-datepicker-toggle matSuffix [for]="pickerTo"></mat-datepicker-toggle>
			<mat-datepicker #pickerTo></mat-datepicker>
		</mat-form-field>
		<button mat-icon-button matTooltip="Clear dates" *ngIf="dateFrom || dateTo"
			(click)="dateFrom = null; dateTo = null">
			<mat-icon>clear</mat-icon>
		</button>
	</div>

	<!-- STEP 1: LOAD -->
	<div class="va-step va-step-load">
		<div class="va-step-label">
			<span class="va-step-num">1</span> Load
		</div>
		<div class="va-step-body">
			<button mat-raised-button color="warn" (click)="loadBackorderAuths()" [disabled]="loading" class="va-btn-primary">
				<mat-spinner *ngIf="loading && loadSource === 'backorder'" diameter="18"></mat-spinner>
				<mat-icon *ngIf="!(loading && loadSource === 'backorder')">search</mat-icon>
				Load All Backorder Auths (status 51)
			</button>
			<span class="va-step-or">or</span>
			<div class="va-manual-wrap">
				<mat-form-field appearance="outline" class="va-textarea-field">
					<mat-label>Specific order numbers (comma / newline / space)</mat-label>
					<textarea matInput [(ngModel)]="orderInput" rows="3" placeholder="1659804, 1659579..."></textarea>
					<mat-hint *ngIf="parsedCount > 0">{{ parsedCount }} order(s) parsed</mat-hint>
				</mat-form-field>
				<button mat-stroked-button color="primary" (click)="loadAuths()" [disabled]="loading || parsedCount === 0">
					<mat-spinner *ngIf="loading && loadSource === 'manual'" diameter="18"></mat-spinner>
					<mat-icon *ngIf="!(loading && loadSource === 'manual')">filter_list</mat-icon>
					Load from List
				</button>
			</div>
		</div>
	</div>

	<ng-container *ngIf="loaded">
		<!-- STEP 2: VALIDATE -->
		<div class="va-step va-step-validate">
			<div class="va-step-label">
				<span class="va-step-num">2</span> Validate
			</div>
			<div class="va-step-body">
				<button mat-raised-button color="accent" (click)="validateAll()"
					[disabled]="validating || items.length === 0 || validated"
					class="va-btn-primary">
					<mat-spinner *ngIf="validating" diameter="18"></mat-spinner>
					<mat-icon *ngIf="!validating">verified</mat-icon>
					{{ validating ? 'Checking Stripe (' + items.length + ' auths)…' : validated ? 'Validated ✓' : 'Validate with Stripe (' + items.length + ')' }}
				</button>
				<div class="va-validate-summary" *ngIf="validated">
					<span class="va-badge va-badge-open">{{ voidableCount }} voidable</span>
					<span class="va-badge va-badge-cancelled" *ngIf="alreadyCancelledCount > 0">{{ alreadyCancelledCount }} already cancelled</span>
					<span class="va-badge va-badge-captured" *ngIf="alreadyCapturedCount > 0">{{ alreadyCapturedCount }} already captured</span>
					<span class="va-badge va-badge-error" *ngIf="validateErrorCount > 0">{{ validateErrorCount }} lookup errors</span>
				</div>
			</div>
		</div>

		<!-- STEP 3: VOID -->
		<div class="va-step va-step-void" [class.va-step-disabled]="!validated">
			<div class="va-step-label">
				<span class="va-step-num">3</span> Void
			</div>
			<div class="va-step-body">
				<div class="va-void-controls">
					<div class="va-void-stats">
						<div class="va-stat" [ngClass]="voidableCount > 0 ? 'va-stat-warn' : 'va-stat-ok'">
							<span class="va-stat-val">{{ voidableCount }}</span>
							<span class="va-stat-label">{{ validated ? 'Voidable' : 'Pending' }}</span>
						</div>
						<div class="va-stat va-stat-money" *ngIf="voidableCount > 0">
							<span class="va-stat-val">{{ fmt(pendingTotal) }}</span>
							<span class="va-stat-label">Total</span>
						</div>
						<div class="va-stat va-stat-ok" *ngIf="voidedCount > 0">
							<span class="va-stat-val">{{ voidedCount }}</span>
							<span class="va-stat-label">Voided</span>
						</div>
					</div>
					<button mat-raised-button color="warn"
						(click)="voidAll()"
						[disabled]="voidableCount === 0"
						[matTooltip]="validated ? 'Void ' + voidableCount + ' validated open auths' : 'Validate first to confirm which auths are still open'">
						<mat-icon>block</mat-icon>
						Void All ({{ voidableCount }})
					</button>
				</div>
				<p class="va-validate-warning" *ngIf="!validated">
					<mat-icon>warning</mat-icon>
					Validate first — many of these may already be cancelled on Stripe.
				</p>
			</div>
		</div>

		<!-- TABLE -->
		<div class="va-empty" *ngIf="items.length === 0">
			<mat-icon>check_circle_outline</mat-icon>
			<span>No open authorizations found.</span>
		</div>

		<div class="va-table-wrap" *ngIf="items.length > 0">
			<table class="va-table">
				<thead>
					<tr>
						<th>Order</th>
						<th>Customer</th>
						<th>Auth Date</th>
						<th class="text-right">Amount</th>
						<th>DB Status</th>
						<th *ngIf="validated">Stripe Status</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let item of items" [ngClass]="rowClass(item)">
						<td class="va-orderno">
							<strong>{{ item.orderno }}</strong>
							<div class="va-debtorno">{{ item.debtorno }}</div>
						</td>
						<td>{{ item.customer_name || '—' }}</td>
						<td>{{ item.transdate | date:'shortDate' }}</td>
						<td class="text-right va-amount">{{ fmt(item.auth_amount) }}</td>
						<td>
							<span class="va-status-badge" *ngIf="!item.voided && !item.void_error">{{ item.auth_status }}</span>
							<span class="va-status-badge va-status-voided" *ngIf="item.voided">
								<mat-icon>block</mat-icon> Voided
							</span>
							<span class="va-status-badge va-status-error" *ngIf="item.void_error && !item.voided" [matTooltip]="item.void_error">
								<mat-icon>error</mat-icon> Failed
							</span>
						</td>
						<td *ngIf="validated">
							<span *ngIf="item.validated" class="va-stripe-badge" [ngClass]="stripeStatusClass(item)">
								{{ stripeStatusLabel(item) }}
							</span>
							<span *ngIf="!item.validated" class="va-stripe-badge va-stripe-pending">—</span>
						</td>
						<td class="va-action-col">
							<button mat-raised-button color="warn"
								*ngIf="!item.voided && (!validated || item.voidable)"
								[disabled]="!!item.voiding"
								(click)="void(item)">
								<mat-spinner *ngIf="item.voiding" diameter="18"></mat-spinner>
								<mat-icon *ngIf="!item.voiding">block</mat-icon>
								{{ item.voiding ? 'Voiding…' : 'Void' }}
							</button>
							<span *ngIf="item.voided" class="va-done-label">
								<mat-icon>done</mat-icon> Done
							</span>
							<span *ngIf="!item.voided && validated && !item.voidable" class="va-skip-label"
								[matTooltip]="item.validate_note || stripeStatusLabel(item)">
								<mat-icon>remove_circle_outline</mat-icon> Skip
							</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</ng-container>
</div>
